import { useCallback, useEffect, useRef, useState } from "react";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";
import { handlePromise } from "../../../utils/handlePromisse";
import {
    allowEmail,
    DcoEmailsRequest,
    deleteEmail,
    deleteTagByEmail,
    FilterDcoEmails,
    getDcoEmails,
    getDcoTags,
} from "../../../services/dco-emails/dco-emails.service";
import { DcoModel } from "./table/dco.model";
import { DcoFormHandle } from "./form/dco-form";

/** Remove diacríticos + lowercase — busca case/accent-insensitive. */
function normalize(str: string): string {
    return str
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
}

/** Estado, fetch e handlers da página de e-mails DCO. */
export function useDcoPage() {
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState<number | undefined>(undefined);
    const [list, setList] = useState<PaginatedResponse<DcoModel>>(initialPagination);
    const [tags, setTags] = useState<string[]>([]);
    const [filter, setFilter] = useState<FilterDcoEmails>({ page: 1, size: 10 });
    const [removeTag, setRemoveTag] = useState<string>("");
    const [tagDialog, setTagDialog] = useState<{ show: boolean; tag: string }>({ show: false, tag: "" });
    const [modalOpen, setModalOpen] = useState(false);

    const formRef = useRef<DcoFormHandle>(null);

    const fetchDcoEmails = useCallback(async () => {
        setLoading(true);
        const [result, error] = await handlePromise(getDcoEmails(filter));
        setLoading(false);

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }

        setList(result.data as PaginatedResponse<DcoModel>);
    }, [filter]);

    const fetchDcoTags = useCallback(async () => {
        const [result, error] = await handlePromise(getDcoTags());
        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }
        setTags(result.data as string[]);
    }, []);

    const onHandleEmailAdd = useCallback(
        async (request: DcoEmailsRequest[]) => {
            setLoading(true);
            const [_, error] = await handlePromise(allowEmail(request));
            setLoading(false);

            if (!!error) {
                setShowSnackbar(true);
                setSnackbarMessage(error.response.data.message);
                return;
            }

            setShowSnackbar(true);
            const plural = `${request.length > 1 ? "s" : ""}`;
            setSnackbarMessage(`Email${plural} adicionado${plural} com sucesso!`);
            setModalOpen(false);
            await fetchDcoEmails();
        },
        [fetchDcoEmails]
    );

    const onDeleteEmailsByTag = async () => {
        const [_, error] = await handlePromise(deleteTagByEmail(tagDialog.tag));

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }

        setShowSnackbar(true);
        setSnackbarMessage(`Emails removidos com sucesso!`);
        setTagDialog({ show: false, tag: "" });
        await fetchDcoEmails();
    };

    const handlePageChange = useCallback((newPage: number) => {
        setFilter((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handlePageSizeChange = useCallback((newSize: number) => {
        setFilter((prev) => ({ ...prev, size: newSize, page: 1 }));
    }, []);

    const onDeleteEmail = async () => {
        setLoading(true);
        const [_, error] = await handlePromise(deleteEmail(deleteId || 0));
        setLoading(false);

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }

        setRemoveTag("");
        setShowSnackbar(true);
        setSnackbarMessage(`Email removido com sucesso!`);
        setShowDeleteDialog(false);
        await fetchDcoEmails();
    };

    const handleDeleteEmail = (id: number) => {
        setShowDeleteDialog(true);
        setDeleteId(id);
    };

    const handleSearch = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const displayedRows = searchValue.trim()
        ? list.data.filter((row) => {
              const needle = normalize(searchValue.trim());
              return normalize(row.email).includes(needle) || (row.tag ? normalize(row.tag).includes(needle) : false);
          })
        : list.data;

    useEffect(() => {
        fetchDcoEmails();
        fetchDcoTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return {
        loading,
        searchValue,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        showDeleteDialog,
        setShowDeleteDialog,
        list,
        tags,
        removeTag,
        setRemoveTag,
        tagDialog,
        setTagDialog,
        modalOpen,
        setModalOpen,
        formRef,
        onHandleEmailAdd,
        onDeleteEmailsByTag,
        handlePageChange,
        handlePageSizeChange,
        onDeleteEmail,
        handleDeleteEmail,
        handleSearch,
        displayedRows,
    };
}
